import { IconButton } from '@creditornot/cb-button';
import {
  Bell,
  ChainLink,
  Download,
  ImageStack,
  MoreHorizontal,
  Plus,
  RotatingArrows,
  ShareAndroid,
  Upload,
} from '@creditornot/cb-icons';
import { MenuActionItem, MenuItemGroup, MenuActionItemVariant } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { Tag, TagVariant } from '@creditornot/cb-tag';
import { useAuthState } from '@creditornot/wolt-auth/react';
import { useQuery } from '@tanstack/react-query';
import React, { useState, useContext, useMemo, ReactNode, useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { exportMenu, getMenuConfigs } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { ConfirmModal } from 'common/components';
import { Typography } from 'common/typography';
import { download } from 'common/utils';
import { useMenuActionsTracking } from 'modules/menu/hooks/telemetryHooks';

import ExportMenuWithDatetimeModal from './ExportMenuWithDatetime';
import ImportMenuModal from './ImportMenuModal';
import { MenuFeaturesContext } from './MenuFeaturesContext';
import { MenuPermissionsContext } from './MenuPermissionsContext';
import { MenuMetaContext } from '../MenuMetaContext';
import { reportMenuExport, reportCsvItemExport, reportCsvItemUpdate, reportMenuSyncActionOpen } from '../behavioral';
import AddToCatalogModal from '../catalog/components/AddToCatalogModal';
import BrokenChainlinkIcon from '../catalog/components/BrokenChainlinkIcon';
import ConnectToCatalogModal from '../catalog/components/ConnectToCatalogModal';
import DisconnectFromCatalogModal from '../catalog/components/DisconnectFromCatalogModal';
import { useMenuModuleContext } from '../context';
import CSVItemDetailsDownloadModal from '../csv-items/CSVItemDetailsDownloadModal';
import CSVItemDetailsUploadModal from '../csv-items/CSVItemDetailsUploadModal';
import CSVItemImportModal from '../csv-items/CSVItemImportModal';
import { useBootstrapMenu } from '../hooks/useBootstrapMenu';
import { useIsBootstrappingToSelectionEnabled } from '../hooks/useIsBootstrappingToSelectionEnabled';
import { useMenuRootRoute } from '../hooks/useMenuRootRoute';
import { orderItemsByBindings } from '../items/utils';
import CancelPushesModal from '../menu-sync/CancelPushesModal';
import { useMenuSyncContext } from '../menu-sync/MenuSyncContext';
import useMenuData from '../useMenuData';
import { flattenCategories } from '../utils';

type Props = {
  venueSlug: string;
  country: string;
  toggleButtonSize?: 'tiny' | 'small';
  refetchInventory: () => void;
  refetchItemConfigs: () => void;
  refetchItemCommissions: () => void;
  refetchMenu: () => void;
};

type Action = {
  icon: ReactNode;
  name: string;
  onClick: () => void;
  variant?: MenuActionItemVariant;
  extraTagLabel?: string;
  extraTagVariant?: TagVariant;
};

const ToggleButton = styled(IconButton).attrs({ variant: 'brand' })``;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px;
`;

const ActionGroup = styled(MenuItemGroup)`
  & + & {
    margin-block-start: 16px;
  }
`;

function isTruthy<T>(argument: T | boolean | undefined): argument is T {
  return !!argument;
}

const MenuActions = ({
  venueSlug,
  country,
  toggleButtonSize = 'tiny',
  refetchInventory,
  refetchItemConfigs,
  refetchItemCommissions,
  refetchMenu,
}: Props) => {
  const history = useHistory();
  const { user } = useAuthState();
  const roles = user?.roles || [];
  const menuPermissions = useContext(MenuPermissionsContext);
  const menuFeatures = useContext(MenuFeaturesContext);
  const { menuId } = useContext(MenuMetaContext);
  const { syncStatus } = useMenuSyncContext();
  const {
    venue: { id: venueId },
  } = useMenuModuleContext();

  const isBootstrappingToSelectionEnabled = useIsBootstrappingToSelectionEnabled();
  const bootstrapMenu = useBootstrapMenu({ onSuccess: () => setShownModal(null) });

  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });

  const { menu, items: allItems, categories, refetchMenuData } = useMenuData();

  if (!menu) {
    throw new Error('Menu not found, this should not happen!');
  }

  const [showActionDropdown, setShowActionDropdown] = useState(false);

  const closeActionDropdown = () => setShowActionDropdown(false);
  const [isCurrentModalOpen, setIsCurrentModalOpen] = useState(true);

  const [shownModal, setShownModal] = useState<
    | 'feedItemImport'
    | 'itemDownload'
    | 'itemImport'
    | 'itemUpdate'
    | 'menuExportWithDatetime'
    | 'menuSync'
    | 'cancelMenuSync'
    | 'menuImport'
    | 'addToCatalog'
    | 'connectToCatalog'
    | 'disconnectFromCatalog'
    | 'bootstrapToSelection'
    | null
  >(null);

  const closeModal = () => setShownModal(null);

  const { data: menuConfigs } = useQuery(['menu-configs', menuId], () => getMenuConfigs(menuId), {
    staleTime: Infinity,
  });

  const csvImportFields = useMemo(() => {
    if (!menuConfigs || (shownModal !== 'itemImport' && shownModal !== 'feedItemImport')) {
      return [];
    }
    const fieldsData =
      shownModal === 'itemImport'
        ? menuConfigs.default_csv_config.fields
        : menuConfigs.source_config?.csv_config.fields || [];
    return fieldsData.map(field => ({
      name: field.input_field_name,
      exampleValue: field.example_value,
      info: field.info,
      required: field.required,
    }));
  }, [menuConfigs, shownModal]);

  const allCategories = useMemo(() => flattenCategories(categories), [categories]);

  const items = useMemo(
    () => orderItemsByBindings(allItems, allCategories).map(item => item),
    [allItems, allCategories],
  );

  const language = menu.primary_language;
  const {
    trackCreateItemsClick,
    trackExportMenuAsJson,
    trackImportImages,
    trackStartImportMenuAsJson,
    trackStartUpdateItemsAsCsv,
  } = useMenuActionsTracking();

  const handleMenuExport = async () => {
    try {
      const menuData = await exportMenu(menuId);
      const json = JSON.stringify(menuData);
      download([json], 'application/json', `${venueSlug}.json`);
      reportMenuExport();
      trackExportMenuAsJson();
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'errors.exportMenuFailed' }), { duration: Infinity });
    }
  };

  const closeModalWithMenuReload = async (reloadMenu: boolean) => {
    closeModal();

    if (reloadMenu) {
      refetchMenu();
      refetchInventory();
      if (menuPermissions.customCommissionRead) {
        refetchItemCommissions();
      }
      refetchItemConfigs();
    }
  };

  const showMenuSyncAction =
    syncStatus &&
    menuPermissions.menuSync &&
    syncStatus.status !== 'push-in-progress' &&
    syncStatus.status !== 'pull-in-progress' &&
    syncStatus.status !== 'push-scheduled' &&
    syncStatus.status !== 'pull-scheduled';

  const showCancelMenuSyncsAction =
    syncStatus &&
    menuPermissions.menuSync &&
    (syncStatus.status === 'push-in-progress' || syncStatus.status === 'push-scheduled');

  const showPastMenuExportAction =
    // TODO: figure out what resources to check so devs & tech support have access
    // so we can stop checking against roles directly
    roles.includes('developer') || roles.includes('developer_merchant') || roles.includes('tech_support_specialist');
  const { baseUrl, entityUrl, isExperienceFlow } = useMenuRootRoute();
  const availableActionGroups: { title: string; actions: Action[] }[] = [
    menuPermissions.exportImport && {
      title: 'menu.actions.menu.title',
      actions: [
        showMenuSyncAction && {
          icon: <ChainLink />,
          name: 'menu.actions.menu.copy',
          variant: 'blue',
          onClick: () => {
            reportMenuSyncActionOpen('start');
            history.push(`${baseUrl}/menu-copy`);
          },
        },
        showCancelMenuSyncsAction && {
          icon: <ChainLink />,
          name: 'menu.actions.menu.cancel-copy',
          variant: 'red',
          onClick: () => {
            setShownModal('cancelMenuSync');
            reportMenuSyncActionOpen('cancel');
          },
        },
        {
          icon: <Download />,
          name: 'menu.actions.menu.export-json',
          onClick: handleMenuExport,
        },
        {
          icon: <Upload />,
          name: 'menu.actions.menu.import-json',
          onClick: () => {
            trackStartImportMenuAsJson();
            setShownModal('menuImport');
          },
        },
        menuPermissions.contentEdit && {
          icon: <ImageStack />,
          name: 'menu.actions.menu.import-images',
          onClick: () => {
            trackImportImages();
            history.push(`${isExperienceFlow ? baseUrl : entityUrl}/bulk-image-upload`);
          },
        },
        menuPermissions.contentEdit &&
          menuFeatures.restockingRulesEnabled && {
            icon: <Bell />,
            name: 'menu.actions.menu.restockingRules',
            onClick: () => history.push(`${baseUrl}/restocking-rules`),
          },
      ].filter(isTruthy),
    },
    {
      title: 'menu.actions.bulk-csv-tools.title',
      actions: [
        menuPermissions.csvItemImport && {
          icon: <Upload />,
          name: 'menu.actions.bulk-csv-tools.create-items',
          onClick: () => {
            trackCreateItemsClick();
            setShownModal('itemImport');
          },
        },
        !!(menuPermissions.csvItemImport && menuFeatures.hasCatalogSource) && {
          icon: <Upload />,
          name: 'menu.actions.bulk-csv-tools.create-items-catalog',
          onClick: () => setShownModal('feedItemImport'),
        },
        menuPermissions.csvItemUpdate && {
          icon: <RotatingArrows />,
          name: 'menu.actions.bulk-csv-tools.update-items',
          onClick: () => {
            setShownModal('itemUpdate');
            trackStartUpdateItemsAsCsv();
          },
        },
        {
          icon: <Download />,
          name: 'menu.actions.bulk-csv-tools.download-items',
          onClick: () => setShownModal('itemDownload'),
        },
      ].filter(isTruthy),
    },
    (showPastMenuExportAction || menuPermissions.catalogCreate || menuPermissions.catalogRead) && {
      title: 'menu.actions.admin.title',
      actions: [
        showPastMenuExportAction && {
          icon: <Download />,
          name: 'menu.exportPastMenu',
          onClick: () => setShownModal('menuExportWithDatetime'),
        },
        menuPermissions.catalogCreate && {
          icon: <Plus />,
          name: 'menu.actions.admin.push-to-catalog',
          onClick: () => setShownModal('addToCatalog'),
        },
        menuPermissions.catalogRead && {
          icon: <ChainLink />,
          name: 'menu.actions.admin.connect-to-catalog',
          onClick: () => setShownModal('connectToCatalog'),
        },
        menuPermissions.catalogRead && {
          icon: <BrokenChainlinkIcon />,
          name: 'menu.actions.admin.disconnect-from-catalog',
          variant: 'red',
          onClick: () => setShownModal('disconnectFromCatalog'),
        },
        isBootstrappingToSelectionEnabled && {
          icon: <ShareAndroid />,
          name: 'Bootstrap menu into Listing Editor',
          variant: 'blue',
          onClick: () => {
            setShownModal('bootstrapToSelection');
          },
        },
      ].filter(isTruthy),
    },
  ].filter(isTruthy);

  const onSpreadsheetStartSelect = useCallback(() => setIsCurrentModalOpen(false), []);
  const onSpreadsheetEndSelect = useCallback(() => setIsCurrentModalOpen(true), []);

  if (availableActionGroups.length === 0) {
    return null;
  }

  return (
    <>
      <Popover
        content={
          <PopoverContent>
            {availableActionGroups.map(group => (
              <ActionGroup key={group.title} title={t(group.title)}>
                {group.actions.map(action => (
                  <MenuActionItem
                    key={action.name}
                    icon={action.icon}
                    onClick={() => {
                      action.onClick();
                      closeActionDropdown();
                    }}
                    variant={action.variant || 'black'}
                  >
                    <FormattedMessage id={action.name} />
                    {!!action.extraTagLabel && (
                      <Tag variant={action.extraTagVariant ?? 'blue'} size="tiny" style={{ marginInlineStart: '8px' }}>
                        {action.extraTagLabel}
                      </Tag>
                    )}
                  </MenuActionItem>
                ))}
              </ActionGroup>
            ))}
          </PopoverContent>
        }
        open={showActionDropdown}
        onOpenChange={setShowActionDropdown}
        offset={8}
        placement="bottom-end"
        renderArrow={false}
      >
        {({ refProps }) => (
          <ToggleButton {...refProps} size={toggleButtonSize}>
            <MoreHorizontal />
          </ToggleButton>
        )}
      </Popover>

      {shownModal === 'itemDownload' && (
        <CSVItemDetailsDownloadModal
          isOpen
          items={items}
          categories={allCategories}
          language={language}
          venueName={venueSlug}
          onDownload={reportCsvItemExport}
          onClose={closeModal}
        />
      )}

      {shownModal === 'itemUpdate' && (
        <CSVItemDetailsUploadModal
          isOpen={isCurrentModalOpen}
          items={items}
          categories={allCategories}
          language={language}
          venueName={venueSlug}
          onUpload={reportCsvItemUpdate}
          onClose={closeModalWithMenuReload}
          onSpreadsheetSelectEnd={onSpreadsheetEndSelect}
          onSpreadsheetSelectStart={onSpreadsheetStartSelect}
        />
      )}

      {(shownModal === 'itemImport' || shownModal === 'feedItemImport') && (
        <CSVItemImportModal
          isOpen={isCurrentModalOpen}
          mode={shownModal === 'feedItemImport' ? 'catalog' : 'menu'}
          csvFields={csvImportFields}
          language={language}
          onClose={closeModalWithMenuReload}
          menuId={menuId}
          country={country}
          onSpreadsheetSelectEnd={onSpreadsheetEndSelect}
          onSpreadsheetSelectStart={onSpreadsheetStartSelect}
        />
      )}
      {shownModal === 'menuImport' && (
        <ImportMenuModal isOpen onClose={() => closeModalWithMenuReload(false)} menuId={menuId} />
      )}

      {shownModal === 'menuExportWithDatetime' && (
        <ExportMenuWithDatetimeModal onClose={() => closeModalWithMenuReload(false)} venueSlug={venueSlug} />
      )}

      {shownModal === 'cancelMenuSync' && <CancelPushesModal onClose={() => setShownModal(null)} />}

      {shownModal === 'addToCatalog' && <AddToCatalogModal items={items} onClose={() => setShownModal(null)} />}

      {shownModal === 'connectToCatalog' && (
        <ConnectToCatalogModal
          items={items}
          onClose={() => setShownModal(null)}
          onConnectSuccess={() => {
            setShownModal(null);
            refetchMenuData();
          }}
        />
      )}

      {shownModal === 'disconnectFromCatalog' && (
        <DisconnectFromCatalogModal menuId={menuId} onClose={closeModalWithMenuReload} />
      )}

      {shownModal === 'bootstrapToSelection' && (
        <ConfirmModal
          header="Are you sure?"
          description={
            <Typography.Body3>
              This will onboard this menu onto the Offering Platform
              <br />
              The selection will be rendered in the new editor
            </Typography.Body3>
          }
          onCancel={() => setShownModal(null)}
          onConfirm={async () => {
            try {
              await bootstrapMenu.mutateAsync(venueId);
              // reloads page with dev toolbar enabled
              window.location.href = `${history.location.pathname}?devTools`;
            } catch (error) {
              toast.error('Error bootstrapping venue menu', { duration: Infinity });
            }
          }}
          open
          loading={bootstrapMenu.isLoading}
        />
      )}
    </>
  );
};

export default MenuActions;
